import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, LogOut, Menu } from "lucide-react";

import { cn } from "@/lib/utils";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CampaignProvider, useCampaign } from "@/contexts/CampaignContext";

const navigation = [
  { name: "Dashboard", href: "/admin" },
  { name: "Days", href: "/admin/days" },
  { name: "Subscribers", href: "/admin/subscribers" },
  { name: "Email Logs", href: "/admin/logs" },
  { name: "Audit Logs", href: "/admin/audit" },
  { name: "Settings", href: "/admin/settings" },
];

function AdminLayoutContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, logout } = useAdminAuth();
  const { campaigns, selectedCampaignId, setSelectedCampaignId, selectedCampaign, isLoading: campaignLoading } = useCampaign();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('AdminLayout - Current location:', location.pathname);
  }, [location.pathname]);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/admin/login", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Show loading while auth or campaigns are loading
  if (authLoading || campaignLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show loading while redirecting
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Left side - Logo and Campaign Selector (desktop only) */}
            <div className="flex items-center gap-4">
              <span className="text-xl font-bold font-serif">
                Admin
              </span>
              {selectedCampaignId && (
                <Select
                  value={selectedCampaignId}
                  onValueChange={setSelectedCampaignId}
                >
                  <SelectTrigger className="hidden md:flex w-full md:w-[280px]">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "text-sm font-medium font-serif transition-colors hover:text-primary",
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {item.name}
                  </Link>
                );
              })}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-4 text-muted-foreground hover:text-foreground hover:bg-transparent focus-visible:ring-0 focus-visible:outline-none"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem asChild>
                    <Link to="/" className="flex items-center gap-2">
                      <Home className="h-4 w-4" />
                      Landing
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Navigation - Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="font-serif">Navigation</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-4">
                  {/* Campaign Selector in Mobile Menu */}
                  {selectedCampaignId && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Campaign</label>
                      <Select
                        value={selectedCampaignId}
                        onValueChange={(value) => {
                          setSelectedCampaignId(value);
                          setMobileMenuOpen(false);
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map((campaign) => (
                            <SelectItem key={campaign.id} value={campaign.id}>
                              {campaign.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Navigation Links */}
                  <div className="flex flex-col gap-2 border-t pt-4">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "text-sm font-medium font-serif transition-colors hover:text-primary px-3 py-2 rounded-md",
                            isActive
                              ? "text-foreground bg-secondary"
                              : "text-muted-foreground"
                          )}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>

                  {/* Additional Actions */}
                  <div className="flex flex-col gap-2 border-t pt-4">
                    <Link
                      to="/"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md transition-colors"
                    >
                      <Home className="h-4 w-4" />
                      Landing
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 rounded-md transition-colors text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        <div style={{ display: 'none' }}>
          Debug: Current route is {location.pathname}
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <CampaignProvider>
      <AdminLayoutContent />
    </CampaignProvider>
  );
}
